import { useMemo } from 'react';

import {
  type PayerAddressT,
  type PayerDemographicsT,
  type PayerInformationT,
  type PharmacyEligibilityT,
  PharmacyEligibilityStatusT,
  usePatientEligibilitiesQuery,
} from '@/entities/eligibility';
import { WorkspaceSection } from '@/shared/ui/primitives';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Badge } from '@/shared/ui/shadcn/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/shared/ui/shadcn/hover-card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/shadcn/table';

interface EligibilitiesSectionProps {
  userId: string;
}

/**
 * Read-only renderer for ``GET /api/patients/{id}/eligibilities``.
 *
 * Lives inside the Prior Auths tab so the clinician can see *why* a PA
 * fired (or didn't): formulary status, member id, and the channel
 * coverage that picks which pharmacy the PA gate evaluates. Layout
 * mirrors the SureScripts cert-matrix columns the fake DoseSpot
 * admin can load — one column = one ``PayerInformationT`` row here.
 *
 * The list is intentionally flat (no row-click action) — eligibility
 * coverages are not actionable from the clinician workspace today.
 */
export function EligibilitiesSection({ userId }: EligibilitiesSectionProps) {
  const query = usePatientEligibilitiesQuery(userId);

  const items: PayerInformationT[] = useMemo(() => query.data?.items ?? [], [query.data]);

  return (
    <WorkspaceSection title="Eligibilities" description="Active payer coverages from DoseSpot">
      {query.isFetching && !query.isLoading ? (
        <p className="mb-2 text-[10px] text-muted-foreground">Refreshing…</p>
      ) : null}
      <EligibilitiesTable
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        items={items}
      />
    </WorkspaceSection>
  );
}

interface EligibilitiesTableProps {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  items: PayerInformationT[];
}

function EligibilitiesTable({ isLoading, isError, error, items }: EligibilitiesTableProps) {
  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading eligibilities…</div>;
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load eligibilities</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? error.message : 'An unknown error occurred.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (items.length === 0) {
    return <div className="text-sm text-muted-foreground">No active coverages found.</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Payer / Plan</TableHead>
          <TableHead>Member</TableHead>
          <TableHead>BIN / PCN</TableHead>
          <TableHead>Channels</TableHead>
          <TableHead>DoseSpot IDs</TableHead>
          <TableHead>Flags</TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {items.map((item, idx) => (
          <TableRow key={item.PatientEligibilityId ?? `coverage-${idx}`}>
            <TableCell className="align-top">
              <div className="font-medium">{item.PayerName ?? '—'}</div>
              <div className="text-xs text-muted-foreground">{item.PlanName ?? '—'}</div>
              {item.PayerId ? (
                <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                  PayerId {item.PayerId}
                </div>
              ) : null}
            </TableCell>

            <TableCell className="align-top">
              <MemberCell memberId={item.MemberId} demographics={item.Demographics} />
            </TableCell>

            <TableCell className="align-top font-mono text-[11px] text-muted-foreground">
              {item.BIN || item.PCN ? (
                <>
                  <div>BIN {item.BIN ?? '—'}</div>
                  <div>PCN {item.PCN ?? '—'}</div>
                </>
              ) : (
                '—'
              )}
            </TableCell>

            <TableCell className="align-top">
              <ChannelList eligibilities={item.PharmacyEligibilities ?? []} />
            </TableCell>

            <TableCell className="align-top">
              <DoseSpotIdList item={item} />
            </TableCell>

            <TableCell className="align-top">
              <div className="flex flex-wrap gap-1">
                {item.PatientEligibilityId != null ? (
                  <Badge variant="outline" className="font-mono text-[10px]">
                    elig #{item.PatientEligibilityId}
                  </Badge>
                ) : null}
                {item.DemographicChanges ? (
                  <Badge variant="outline" className="text-[10px]">
                    Demographic Change
                  </Badge>
                ) : null}
                {item.ErrorMessage ? (
                  <Badge variant="destructive" className="text-[10px]">
                    {item.ErrorMessage}
                  </Badge>
                ) : null}
                {item.InfoMessage ? (
                  <Badge variant="secondary" className="text-[10px]">
                    {item.InfoMessage}
                  </Badge>
                ) : null}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface ChannelListProps {
  eligibilities: PharmacyEligibilityT[];
}

function ChannelList({ eligibilities }: ChannelListProps) {
  if (eligibilities.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-col gap-0.5">
      {eligibilities.map((entry, idx) => (
        <div
          key={`${entry.PharmacyType ?? 'channel'}-${idx}`}
          className="flex flex-wrap items-baseline gap-x-1 text-xs"
        >
          <span className="font-medium">{entry.PharmacyType ?? '—'}</span>
          <ChannelStatusBadge status={entry.EligibilityStatus} />
          {entry.Name ? (
            <span className="font-mono text-[10px] text-muted-foreground">{entry.Name}</span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ChannelStatusBadge({ status }: { status: PharmacyEligibilityT['EligibilityStatus'] }) {
  if (!status) return null;
  const variant: React.ComponentProps<typeof Badge>['variant'] =
    status === PharmacyEligibilityStatusT.Covered
      ? 'secondary'
      : status === PharmacyEligibilityStatusT.NotCovered
        ? 'destructive'
        : 'outline';
  return (
    <Badge variant={variant} className="text-[10px]">
      {status}
    </Badge>
  );
}

interface DoseSpotIdListProps {
  item: PayerInformationT;
}

function DoseSpotIdList({ item }: DoseSpotIdListProps) {
  const ids: Array<{ label: string; value: string | null | undefined }> = [
    { label: 'Coverage', value: item.CoverageId },
    { label: 'Formulary', value: item.FormularyId },
    { label: 'Alternatives', value: item.AlternativesId },
    { label: 'Copay', value: item.CopayId },
  ];
  const hasAny = ids.some((entry) => entry.value);
  if (!hasAny) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-col gap-0.5 font-mono text-[10px] text-muted-foreground">
      {ids.map((entry) => (
        <div key={entry.label}>
          <span className="text-foreground/70">{entry.label}</span> {entry.value ?? '—'}
        </div>
      ))}
    </div>
  );
}

interface MemberCellProps {
  memberId: PayerInformationT['MemberId'];
  demographics: PayerInformationT['Demographics'];
}

function MemberCell({ memberId, demographics }: MemberCellProps) {
  const fullName = formatDemographicsName(demographics);
  const dob = formatDateOfBirth(demographics?.DateOfBirth);
  const hasDemographics = Boolean(
    demographics &&
    (demographics.LastName ||
      demographics.FirstName ||
      demographics.DateOfBirth ||
      demographics.Address)
  );

  return (
    <div className="flex flex-col gap-0.5">
      {fullName ? (
        hasDemographics ? (
          <HoverCard>
            <HoverCardTrigger asChild>
              <button
                type="button"
                className="text-left text-xs underline-offset-2 hover:underline focus-visible:outline-none focus-visible:underline"
              >
                {fullName}
              </button>
            </HoverCardTrigger>
            <HoverCardContent align="start" className="w-72">
              <DemographicsCard demographics={demographics ?? null} />
            </HoverCardContent>
          </HoverCard>
        ) : (
          <span className="text-xs">{fullName}</span>
        )
      ) : null}
      {dob ? <span className="text-[10px] text-muted-foreground">DOB {dob}</span> : null}
      <span
        className="font-mono text-[11px] break-all text-muted-foreground"
        title={memberId ?? undefined}
      >
        {memberId ?? '—'}
      </span>
    </div>
  );
}

function DemographicsCard({ demographics }: { demographics: PayerDemographicsT | null }) {
  if (!demographics) {
    return <p className="text-xs text-muted-foreground">No demographics returned.</p>;
  }
  const fullName = formatDemographicsName(demographics);
  const dob = formatDateOfBirth(demographics.DateOfBirth);
  const addressLines = formatAddressLines(demographics.Address ?? null);

  return (
    <div className="space-y-2 text-xs">
      <div>
        <div className="font-medium">{fullName ?? '—'}</div>
        <div className="text-muted-foreground">
          {[demographics.Gender, dob ? `DOB ${dob}` : null].filter(Boolean).join(' · ') || '—'}
        </div>
      </div>
      {addressLines.length > 0 ? (
        <div className="text-muted-foreground">
          {addressLines.map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function formatDemographicsName(
  demographics: PayerDemographicsT | null | undefined
): string | null {
  if (!demographics) return null;
  const parts = [
    demographics.FirstName,
    demographics.MiddleName,
    demographics.LastName,
    demographics.Suffix,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' ') : null;
}

function formatDateOfBirth(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${year}-${month}-${day}`;
}

function formatAddressLines(address: PayerAddressT | null): string[] {
  if (!address) return [];
  const lines: string[] = [];
  if (address.AddressLine1) lines.push(address.AddressLine1);
  if (address.AddressLine2) lines.push(address.AddressLine2);
  const cityStateZip = [address.City, [address.State, address.ZipCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  if (cityStateZip) lines.push(cityStateZip);
  return lines;
}
